import { DCMSDocumentDB, Document, unmarshallFile } from "../db";
import { db } from "../db";

import { createContext, ReactNode, useContext } from "react";
import { useLiveQuery } from "dexie-react-hooks";

export interface ViewNode {
  name: string;
  children?: string[];
  modifier?: "AND" | "OR";
  query: "downloads_done" | "downloads" | "recent_changes" | DocumentQueryMatchClause[];
  // showBadge?: (db: DCMSDocumentDB) => Promise<number>;
  icon?: string;
}

export interface Category {
  name: string;
  views: ViewNode[];
}

export interface DocumentQueryMatchClause {
  key: keyof Document;
  values: string[];
}

export interface AppConfiguration {
  appTitle: string;
  /**
   * The amount of time (in seconds) between attempts to synchronise
   */
  syncPeriod: number;
  titleField: string;
  categories: Category[];
  helpDoc?: string;
}

export const defaultAppConfiguration: AppConfiguration = {
  appTitle: "",
  titleField: "title",
  syncPeriod: 60,
  categories: [
    {
      name: "All Documents (Search)",
      views: [
        {
          name: "All Documents (Search)",
          query: [],
          icon: "search",
        },
      ],
    },
    {
      name: "Download Queue",
      views: [
        {
          name: "Download Queue",
          icon: "downloads",
          query: "downloads",
        },
      ],
    },
    {
      name: "Recent Downloads",
      views: [
        {
          name: "Recent Downloads",
          icon: "downloads_done",
          query: "downloads_done",
        },
      ],
    },
    {
      name: "Recently Updated",
      views: [
        {
          name: "Recently Updated",
          icon: "recent_changes",
          query: "recent_changes",
        },
      ],
    },
  ],
};

export const AppConfigurationContext = createContext<AppConfiguration>(defaultAppConfiguration);

const isDocument = (d: Document) => d.key.startsWith("docs/");

function matches(doc: Document, clauses: DocumentQueryMatchClause[], mod: "AND" | "OR" = "AND") {
  if (mod === "OR") {
    for (let clause of clauses) {
      const val = doc[clause.key]?.toString();
      if (val && clause.values.includes(val)) {
        return true;
      }
    }
    return false;
  } else {
    for (let clause of clauses) {
      const val = doc[clause.key]?.toString();
      if (!val || !clause.values.includes(val)) {
        return false;
      }
    }
    return true;
  }
}

export function executeViewQuery(db: DCMSDocumentDB, view: ViewNode): Promise<Document[]> {
  // Deal with special queries.
  if (view.query === "downloads") {
    return db.documents.where("download_order").aboveOrEqual(0).toArray();
  }
  if (view.query === "downloads_done") {
    return db.documents.where("downloaded_at").above(0).reverse().and(isDocument).limit(100).toArray();
  }
  if (view.query === "recent_changes") {
    return db.documents
      .where("modified")
      .above(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString())
      .reverse()
      .and(isDocument)
      .toArray();
  }

  if (!Array.isArray(view.query)) {
    // TODO do more validation of the configuration. (https://app.asana.com/0/1208187577428603/1208328994468384)
    console.log("Query", view.query, "is not valid");
    return Promise.resolve([]);
  }

  // Because we allow _any_ field to be used as index, we must start with _all_ documents, then whittle it down
  // This is not efficient, but we'd have to create index on the fly otherwise.
  return db.documents
    .where("key")
    .startsWith("docs/")
    .filter((doc) => matches(doc, view.query as DocumentQueryMatchClause[], view.modifier))
    .toArray();
}

export function useAppConfiguration() {
  return useContext(AppConfigurationContext);
}

function isDocumentQueryMatchClause(x: any): x is DocumentQueryMatchClause {
  if (typeof x !== "object") {
    return false;
  }
  return typeof x.key === "string" && Array.isArray(x.values) && x.values.every((v: any) => typeof v === "string");
}

function parseViewNode(raw: any): ViewNode | undefined {
  if (typeof raw !== "object") {
    console.log("Rejecting view", raw, "because it is not an object");
    return undefined;
  }
  if (typeof raw.name !== "string") {
    console.log("Rejecting view", raw, "because it's name is not a string");
    return undefined;
  }

  if (!(raw.icon === undefined || ["search", "downloads", "downloads_done", "recent_changes"].includes(raw.icon))) {
    console.log("Rejecting view ", raw.name, "because its icon is wrong");
    return undefined;
  }
  if (!(raw.modifier === undefined || ["AND", "OR"].includes(raw.modifier))) {
    console.log("Rejecting view ", raw.name, "because its modifier field is wrong");
    return undefined;
  }

  if (
    !(
      ["downloads_done", "downloads", "recent_changes"].includes(raw.query) ||
      (Array.isArray(raw.query) && raw.query.find((r: any) => !isDocumentQueryMatchClause(r)) === undefined)
    )
  ) {
    console.log("Rejecting view ", raw.name, "because its query field is wrong");
    return undefined;
  }
  const res = {
    name: raw.name,
    icon: raw.icon,
    modifier: raw.modifier,
    query: raw.query,
    children: raw.children,
  };
  return res;
}

function parseCategory(raw: any): Category | undefined {
  if (typeof raw !== "object") {
    return undefined;
  }
  if (typeof raw.name !== "string") {
    return undefined;
  }
  if (!Array.isArray(raw.views)) {
    return undefined;
  }
  return {
    name: raw.name,
    views: raw.views.map(parseViewNode).filter((v: ViewNode | undefined) => v !== undefined),
  };
}

export async function readConfiguration(key: string, defaultValue: any): Promise<AppConfiguration> {
  try {
    const data = await unmarshallFile(await db.files.get(key));
    if (!data) {
      return defaultValue;
    }
    const txt = new TextDecoder().decode(data);
    const raw = JSON.parse(txt);

    // Rather than just return the raw JSON, which might be invalild, we selectively parse it.
    const res: AppConfiguration = {
      appTitle: raw.appTile ?? "Documents",
      titleField: raw.titleField ?? "title",
      syncPeriod: raw.syncPeriod ?? 60,
      categories: raw.categories.map((raw: any) => parseCategory(raw)).filter((v: Category | undefined) => v !== undefined),
      helpDoc: raw.helpDoc,
    };
    return res;
  } catch (ex) {
    console.log("Error parsing Data", ex);
    return defaultAppConfiguration;
  }
}

export function AppConfigurationProvider(props: { children: ReactNode }) {
  const config = useLiveQuery(
    async () => readConfiguration("meta/config.json", defaultAppConfiguration),
    [],
    defaultAppConfiguration
  );

  return <AppConfigurationContext.Provider value={config}>{props.children}</AppConfigurationContext.Provider>;
}
